// MobileFAB.jsx — Floating Action Button (mobile/tablet ≤1024px only)
// Sits dead-centre above the floating bottom nav pill.
// Position is calculated to sit flush in the centre slot of the nav:
//   nav bottom = 12px, nav height = 62px → nav centre-y = 12 + 31 = 43px from bottom
//   FAB radius = 28px → FAB bottom = 43 - 28 = 15px... but we want it to "pop" slightly
//   above centre, so bottom: 12 + (62/2) - (56/2) = 12 + 31 - 28 = 15px
//
// Quick-add options fan upward from the FAB on tap.
// Calls onQuickAdd(typeId) — the parent (FeedView) handles opening EntryForm.

import { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const QUICK_OPTIONS = [
  { id: 'text',     label: 'Text',     icon: '📄', color: '#888784', bg: 'rgba(95,94,90,0.20)'    },
  { id: 'image',    label: 'Image',    icon: '🖼',  color: '#5dcaa5', bg: 'rgba(29,158,117,0.20)'  },
  { id: 'video',    label: 'Video',    icon: '🎬',  color: '#ef9f27', bg: 'rgba(239,159,39,0.20)'  },
  { id: 'code',     label: 'Code',     icon: '💻',  color: '#85b7eb', bg: 'rgba(133,183,235,0.20)' },
  { id: 'prompt',   label: 'Prompt',   icon: '✦',  color: '#afa9ec', bg: 'rgba(127,119,221,0.20)' },
  { id: 'longform', label: 'Longform', icon: '📝',  color: '#ef9f27', bg: 'rgba(239,159,39,0.20)'  },
];

// Nav geometry constants — must match MobileNav values
const NAV_BOTTOM   = 12;  // px from viewport bottom (above home indicator)
const NAV_HEIGHT   = 62;  // px
const FAB_SIZE     = 56;  // px diameter

// FAB sits centred vertically on the nav, raised 8px above centre to pop above it
const FAB_BOTTOM_PX = NAV_BOTTOM + Math.round(NAV_HEIGHT / 2) - Math.round(FAB_SIZE / 2) + 8;
const FAB_BOTTOM = `calc(${FAB_BOTTOM_PX}px + env(safe-area-inset-bottom, 0px))`;

function useIsMobile() {
  const [ok, setOk] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth <= 1024 : false
  );
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 1024px)');
    const fn = (e) => setOk(e.matches);
    mq.addEventListener('change', fn);
    return () => mq.removeEventListener('change', fn);
  }, []);
  return ok;
}

export default function MobileFAB({ onQuickAdd }) {
  const isMobile     = useIsMobile();
  const [open, setOpen] = useState(false);
  const { user }     = useAuth();
  const navigate     = useNavigate();
  const location     = useLocation();
  const backdropRef  = useRef(null);

  // Close menu when route changes — must be before any early returns
  useEffect(() => { setOpen(false); }, [location.pathname]);

  // Never render on desktop
  if (!isMobile) return null;

  // Hide on same pages as MobileNav
  const path = location.pathname;
  const blocked = ['/', '/auth', '/join', '/auth/callback', '/reset-password', '/setup/username',
                   '/team/create', '/team/new'];
  if (blocked.includes(path)) return null;
  if (path.startsWith('/legal/') || path.startsWith('/entry/')) return null;

  const handleOption = (typeId) => {
    setOpen(false);
    if (!user) { navigate('/auth'); return; }
    // Always call onQuickAdd — parent decides what to do
    // If we're not on /app, navigate there first with the type as a query param
    if (path !== '/app') {
      navigate(`/app?newEntry=${typeId}`);
    } else {
      onQuickAdd && onQuickAdd(typeId);
    }
  };

  // The fan menu should open upward starting just above the FAB
  // FAB top edge from bottom = FAB_BOTTOM + FAB_SIZE
  const menuBottom = `calc(${FAB_BOTTOM_PX + FAB_SIZE + 8}px + env(safe-area-inset-bottom, 0px))`; // 8px gap above FAB

  return (
    <>
      {/* Backdrop */}
      {open && (
        <div
          ref={backdropRef}
          onClick={() => setOpen(false)}
          style={{
            position: 'fixed', inset: 0,
            background: 'rgba(0,0,0,0.48)',
            backdropFilter: 'blur(3px)',
            WebkitBackdropFilter: 'blur(3px)',
            zIndex: 195,
            WebkitTapHighlightColor: 'transparent',
          }}
        />
      )}

      {/* Quick-add option grid — fans upward from FAB */}
      {open && (
        <div style={{
          position: 'fixed',
          bottom: menuBottom,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 210,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 10,
        }}>
          {/* Heading */}
          <span style={{
            fontSize: 10, color: 'rgba(255,255,255,0.45)',
            letterSpacing: '0.08em', textTransform: 'uppercase',
            fontFamily: 'var(--font)', marginBottom: 2,
          }}>
            Quick add
          </span>

          {/* Two rows of 3 */}
          {[QUICK_OPTIONS.slice(0, 3), QUICK_OPTIONS.slice(3)].map((row, ri) => (
            <div key={ri} style={{ display: 'flex', gap: 10 }}>
              {row.map((opt, i) => (
                <button
                  key={opt.id}
                  onClick={() => handleOption(opt.id)}
                  style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5,
                    background: 'var(--surface)',
                    border: `1.5px solid ${opt.color}55`,
                    borderRadius: 16,
                    padding: '11px 13px',
                    minWidth: 70,
                    cursor: 'pointer',
                    outline: 'none',
                    WebkitTapHighlightColor: 'transparent',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.40)',
                    animation: `fabOptionIn 0.20s ease ${(ri * 3 + i) * 0.035}s both`,
                    fontFamily: 'var(--font)',
                    transition: 'transform 0.1s',
                  }}
                  onPointerDown={e => e.currentTarget.style.transform = 'scale(0.93)'}
                  onPointerUp={e => e.currentTarget.style.transform = ''}
                  onPointerLeave={e => e.currentTarget.style.transform = ''}
                >
                  <div style={{
                    width: 38, height: 38, borderRadius: 11,
                    background: opt.bg,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 19,
                  }}>
                    {opt.icon}
                  </div>
                  <span style={{ fontSize: 10.5, color: 'var(--muted)', fontWeight: 500, lineHeight: 1 }}>
                    {opt.label}
                  </span>
                </button>
              ))}
            </div>
          ))}


        </div>
      )}

      {/* The FAB itself — vertically centred in the nav pill */}
      <button
        onClick={() => {
          if (!user) { navigate('/auth'); return; }
          setOpen(o => !o);
        }}
        aria-label={open ? 'Close quick add' : 'Quick add'}
        style={{
          position: 'fixed',
          bottom: FAB_BOTTOM,
          left: '50%',
          transform: `translateX(-50%) rotate(${open ? '45deg' : '0deg'})`,
          zIndex: 220,
          width: FAB_SIZE, height: FAB_SIZE,
          borderRadius: '50%',
          background: open
            ? 'var(--surface2)'
            : 'linear-gradient(140deg, #7f77dd 0%, #9990e6 100%)',
          border: open ? '1.5px solid var(--border2)' : 'none',
          cursor: 'pointer',
          outline: 'none',
          WebkitTapHighlightColor: 'transparent',
          WebkitAppearance: 'none',
          MozAppearance: 'none',
          appearance: 'none',
          boxShadow: open
            ? '0 2px 8px rgba(0,0,0,0.20)'
            : '0 -2px 20px rgba(127,119,221,0.5), 0 0 0 3px rgba(127,119,221,0.15)',
          transition: 'transform 0.22s ease, background 0.22s, box-shadow 0.22s, border 0.22s',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
          stroke={open ? 'var(--muted)' : '#fff'}
          strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
          <line x1="12" y1="5" x2="12" y2="19"/>
          <line x1="5" y1="12" x2="19" y2="12"/>
        </svg>
      </button>

      <style>{`
        @keyframes fabOptionIn {
          from { opacity: 0; transform: translateY(12px) scale(0.86); }
          to   { opacity: 1; transform: translateY(0)    scale(1); }
        }
      `}</style>
    </>
  );
}
